import axios from 'axios';
import L from 'leaflet';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import 'leaflet/dist/leaflet.css';
import React, { useEffect, useRef, useState } from 'react';
import { MapContainer, Marker, Popup, TileLayer } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-markercluster';
import { useCityLocation } from '../../contexts/CityLocationContext';
import { Event, GeocoderUtility, Location } from '../../utils/GeocoderUtility';
import './EventsSegment.css';

// Fix for default marker icon issue with Leaflet and Webpack
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png',
});

// EventFilters component
const EventFilters: React.FC<{
  onFilterChange: (filters: { dateRange: string }) => void
}> = ({ onFilterChange }) => {
  return (
    <div className="fixed mt-40 right-4 z-[999] bg-white p-2 rounded-lg shadow-lg">
      <select
        className="w-full p-2 border rounded"
        onChange={(e) => onFilterChange({ dateRange: e.target.value })}
      >
        <option value="today">Today</option>
        <option value="tomorrow">Tomorrow</option>
        <option value="week">This Week</option>
        <option value="month">This Month</option>
      </select>
    </div>
  );
};

function EventsSegment() {
  const [location, setLocation] = useState<Location | null>(null);
  const { cityLocation, setCityLocation } = useCityLocation();
  const [events, setEvents] = useState<Event[]>([]);
  const [filters, setFilters] = useState({ dateRange: 'week' });
  const isFirstRun = useRef(true);

  // Get user's location and convert to city
  useEffect(() => {
    if (isFirstRun.current) {
      isFirstRun.current = false;

      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          async (position) => {
            const { latitude, longitude } = position.coords;
            setLocation([latitude, longitude]);

            try {
              const response = await axios.get(
                `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`
              );

              if (response.data?.address) {
                const { village, state } = response.data.address;
                if (village && state) {
                  setCityLocation(`${village}, ${state}`);
                  // TODO Tell PlayButton to use this location
                }
              }
            } catch (error) {
              console.error('Error getting city:', error);
            }
          },
          (error) => {
            console.error(error);
            setLocation(null);
          }
        );
      }
    }
  }, [setCityLocation]);

  // Fetch events when city location changes
  useEffect(() => {
    const fetchEvents = async () => {
      if (!cityLocation) return;

      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/events`, {
          withCredentials: true,
          params: {
            q: 'events',
            location: cityLocation,
            htichips: `date:${filters.dateRange}`,
            forceUser: `${JSON.parse(process.env.REACT_APP_USER ? process.env.REACT_APP_USER : '')?.email}`,
          }
        });

        const rawEvents = response.data.events_results || [];
        const processedEvents = await GeocoderUtility.updateEventCoordinates(rawEvents);
        setEvents(processedEvents);
      } catch (error) {
        console.error('Error fetching events:', error);
      }
    };

    fetchEvents();
  }, [cityLocation, filters.dateRange]);

  const eventIcon = new L.Icon({
    iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-blue.png',
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
  });

  return (
    <div className="Event">
      <main className="relative pt-12">
        {location ? (
          <>
            <EventFilters onFilterChange={setFilters} />
            <MapContainer
              center={location}
              zoom={12}
              className="h-[calc(100vh-96px)]" /* Adjusted height to account for header */
            >
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              />

              {/* User location marker */}
              <Marker position={location}>
                <Popup>
                  You are here
                </Popup>
              </Marker>


              {/* Event markers */}
              <MarkerClusterGroup
                chunkedLoading
                iconCreateFunction={(cluster: L.MarkerCluster): L.DivIcon => {
                  return new L.DivIcon({
                    html: `<div class="cluster-icon">${cluster.getChildCount()}</div>`,
                    className: 'custom-cluster-icon',
                    iconSize: L.point(40, 40)
                  });
                }}
                maxClusterRadius={60}
              >
                {events.map((event, index) => {
                  return event.coordinates ? (
                    <Marker
                      key={index}
                      position={event.coordinates}
                      icon={eventIcon}
                    >
                      <Popup>
                        <div className="max-w-md">
                          <h3 className="font-bold text-lg">{event.title}</h3>
                          <p className="text-sm text-gray-600">
                            {event.date.when || event.date.start_date || 'Date not specified'}
                          </p>
                          {event.venue?.name && (
                            <p className="text-sm">
                              Venue: {event.venue.name}
                              {event.venue.rating && ` (${event.venue.rating}★)`}
                            </p>
                          )}
                          {event.description && (
                            <p className="text-sm mt-2">{event.description}</p>
                          )}
                          {event.ticket_info?.price && (
                            <p className="text-sm mt-2">
                              Price: {event.ticket_info.price}
                            </p>
                          )}
                          <div className="mt-2">
                            <a
                              href={event.link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-500 hover:text-blue-700 text-sm"
                            >
                              More Info
                            </a>
                          </div>
                        </div>
                      </Popup>
                    </Marker>
                  ) : null
                })}

              </MarkerClusterGroup>
            </MapContainer>
          </>
        ) : (
          <p>Loading location...</p>
        )}
      </main>
    </div>


  );
}

export default EventsSegment;